/**
 * A contract's inspections — `inspections/contract/:contractId` (PLAN §7).
 *
 * This round's status, the car's open items with their photos and "open since", Inspect and
 * Re-check, and the certificate history (tap any photo to zoom). Read through
 * get_contract_inspections, which gives an admin the car's view: every visit on the vehicle, on this
 * contract, on others and between contracts, each marked. Inspect is offered only when the board's
 * row for this contract reads `in_progress` (get_contract_inspections carries no such key): a
 * contract that owed the round and has since ended takes no monthly, and a Re-check on its car is
 * recorded on the car's contract in progress, or between contracts.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Camera, ClipboardCheck, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
    capturePath,
    carriedLabel,
    formatInspectionDay,
    formatShortDay,
    inProgressOnBoard,
    lineLabel,
    offersMonthly,
    roundName,
    windowLabel,
} from '@/lib/inspections';
import { browserStorage, deleteDraft, isResumable, readDraft } from '@/lib/inspectionDraft';
import { coverExpiryStatus } from '@/lib/coverExpiry';
import { useContractInspections, useInspectionBoard } from '@/components/inspections/inspectionApi';
import { AttendanceBadge, ConditionBadge, GradeBadge, OpenCounts, ResultBadge } from '@/components/inspections/InspectionChip';
import { PhotoStrip } from '@/components/inspections/PhotoZoom';
import InspectionCertificate from '@/components/inspections/InspectionCertificate';

export default function InspectionContract() {
    const { contractId: param } = useParams();
    const contractId = Number(param) || 0;
    const navigate = useNavigate();
    const location = useLocation();
    const { data, isLoading, error } = useContractInspections(contractId);
    const board = useInspectionBoard(null);
    const [draftTick, setDraftTick] = useState(0);

    // A link to one certificate (#visit-<id>) scrolls to it once the history has loaded.
    useEffect(() => {
        if (!data || !location.hash) return;
        const el = document.getElementById(location.hash.slice(1));
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [data, location.hash]);

    const vehicleId = data?.header.vehicle_id ?? null;
    // Drafts live in this browser only. `draftTick` re-reads storage after a discard.
    const drafts = useMemo(() => {
        if (!vehicleId || draftTick < 0) return { monthly: null, recheck: null };
        const storage = browserStorage();
        return { monthly: readDraft(storage, vehicleId, 'monthly'), recheck: readDraft(storage, vehicleId, 'recheck') };
    }, [vehicleId, draftTick]);

    if (error) {
        return (
            <Alert variant="destructive" className="mx-auto mt-4 max-w-3xl">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>These inspections could not be loaded: {(error as Error).message}</AlertDescription>
            </Alert>
        );
    }
    if (isLoading || board.isLoading) {
        return (
            <div className="mx-auto max-w-3xl space-y-4 pt-2">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }
    if (!data) {
        return (
            <div className="mx-auto max-w-3xl space-y-3 pt-6 text-center">
                <p className="text-muted-foreground">No contract #{contractId}.</p>
                <Button variant="outline" onClick={() => navigate('/dashboard/inspections')}>
                    Back to the board
                </Button>
            </div>
        );
    }

    const { header, current, open_items: openItems, visits, checklist } = data;
    const canMonthly = offersMonthly(current.attendance, current.condition === 'held', inProgressOnBoard(board.data, data.contract_id)) && !!vehicleId;
    const windowText = windowLabel(current.opens_on, current.closes_on);
    const nextWindow = windowLabel(current.next_opens_on, current.next_closes_on);
    // Good through its expiry date, on the Ghana day (G6) — the rule this page already kept, now the shared one.
    const roadworthyPast = coverExpiryStatus(header.roadworthy_expires_on, current.today) === 'expired';
    const draftList = [drafts.monthly, drafts.recheck].filter((d): d is NonNullable<typeof d> => !!d);

    return (
        <div className="mx-auto max-w-3xl animate-fade-in space-y-6 pb-8 sm:pt-2">
            <div className="space-y-3">
                <Link to="/dashboard/inspections" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    Inspections board
                </Link>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-3xl font-semibold tracking-tight">{header.plate ?? 'Unregistered'}</h1>
                        <p className="text-muted-foreground">
                            {[header.make, header.model].filter(Boolean).join(' ')}
                            {header.driver_name && ` · ${header.driver_name}`} · contract #{data.contract_id}
                        </p>
                        <p className="mt-1 text-sm">
                            <span className={cn(roadworthyPast && 'font-medium text-red-700 dark:text-red-400')}>
                                {header.roadworthy_expires_on
                                    ? `Roadworthy ${roadworthyPast ? 'expired' : 'until'} ${formatInspectionDay(header.roadworthy_expires_on)}`
                                    : 'No roadworthy on file'}
                            </span>
                            {header.insurance_expires_on && (
                                <span className="text-muted-foreground"> · Insurance until {formatInspectionDay(header.insurance_expires_on)}</span>
                            )}
                        </p>
                    </div>
                    <ConditionBadge condition={current.condition} className="text-sm" />
                </div>
            </div>

            {/* This round. */}
            <section className="space-y-4 rounded-xl border bg-card p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="font-semibold">{current.period ? roundName(current.period) : 'No round yet'}</h2>
                        <p className="text-sm text-muted-foreground">
                            {current.period
                                ? `Window ${windowText}`
                                : nextWindow
                                  ? `The first round’s window is ${nextWindow}.`
                                  : 'Monthly inspections start with the first round.'}
                        </p>
                    </div>
                    <AttendanceBadge attendance={current.attendance} late={current.late} />
                </div>

                <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                    {current.inspection_id && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            <dt className="text-muted-foreground">This round’s monthly</dt>
                            <dd className="inline-flex items-center gap-1.5">
                                <a href={`#visit-${current.inspection_id}`} className="underline underline-offset-2">
                                    {formatShortDay(current.inspected_on)}
                                </a>
                                <ResultBadge result={current.result} />
                            </dd>
                        </div>
                    )}
                    {current.released_on && (
                        <div className="flex flex-wrap gap-1.5">
                            <dt className="text-muted-foreground">Re-checked and released</dt>
                            <dd>{formatShortDay(current.released_on)}</dd>
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5">
                        <dt className="text-muted-foreground">Open on the car</dt>
                        <dd>
                            <OpenCounts fixNow={current.open_fix_now} fixSoon={current.open_fix_soon} />
                        </dd>
                    </div>
                    {current.period && nextWindow && (
                        <div className="flex flex-wrap gap-1.5">
                            <dt className="text-muted-foreground">Next window</dt>
                            <dd>{nextWindow}</dd>
                        </div>
                    )}
                </dl>

                {vehicleId && (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        {canMonthly && (
                            <Button className="h-11 sm:flex-1" onClick={() => navigate(capturePath(vehicleId, 'monthly', data.contract_id))}>
                                <Camera aria-hidden />
                                {drafts.monthly && isResumable(drafts.monthly, current.today) ? 'Resume inspection' : 'Inspect'}
                            </Button>
                        )}
                        <Button
                            variant={canMonthly ? 'outline' : 'default'}
                            className="h-11 sm:flex-1"
                            onClick={() => navigate(capturePath(vehicleId, 'recheck', data.contract_id))}
                        >
                            <RotateCcw aria-hidden />
                            {drafts.recheck && isResumable(drafts.recheck, current.today) ? 'Resume re-check' : 'Re-check'}
                        </Button>
                    </div>
                )}

                {draftList.length > 0 && vehicleId && (
                    <div className="space-y-2 rounded-lg bg-muted/50 p-3 text-sm">
                        {draftList.map((d) => (
                            <div key={d.kind} className="flex flex-wrap items-center justify-between gap-2">
                                <span>
                                    A {d.kind === 'monthly' ? 'monthly' : 're-check'} draft from {formatShortDay(d.startedOn)} is saved in this browser
                                    {isResumable(d, current.today) ? '.' : ', too old to resume.'}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        deleteDraft(browserStorage(), vehicleId, d.kind);
                                        setDraftTick((t) => t + 1);
                                    }}
                                >
                                    Discard
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* The car's open items. */}
            <section className="space-y-3">
                <h2 className="text-lg font-semibold tracking-tight">Open items on the car ({openItems.length})</h2>
                {openItems.length === 0 ? (
                    <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">Nothing is open.</p>
                ) : (
                    <ul className="space-y-2">
                        {openItems.map((o) => {
                            const carried = carriedLabel(o.times_carried);
                            return (
                                <li
                                    key={o.item}
                                    className={cn('rounded-xl border border-l-4 bg-card p-3 sm:p-4', o.grade === 'fix_now' ? 'border-l-red-500' : 'border-l-amber-500')}
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <GradeBadge grade={o.grade} />
                                        <span className="font-medium">{lineLabel(checklist, o.item)}</span>
                                    </div>
                                    {o.note && <p className="mt-1 text-sm">{o.note}</p>}
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        Open since {formatShortDay(o.raised_on)}
                                        {carried && ` · ${carried}`}
                                        {o.raised_before_contract && ' · raised before this contract'}
                                    </p>
                                    <PhotoStrip photos={o.photos.map((p) => ({ id: p.id, url: p.url }))} label={lineLabel(checklist, o.item)} className="mt-2" size="h-16 w-16" />
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {/* Certificate history. */}
            <section className="space-y-3">
                <h2 className="text-lg font-semibold tracking-tight">Inspections ({visits.length})</h2>
                {visits.length === 0 ? (
                    <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
                        <ClipboardCheck className="mx-auto mb-2 h-6 w-6 opacity-40" aria-hidden />
                        <p className="text-sm">No inspection on this car yet.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {visits.map((v) => (
                            <InspectionCertificate key={v.id} visit={v} checklist={checklist} contractId={data.contract_id} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
